/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import produce from 'immer';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult } from 'react-query';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { DeleteButton, EditButton, Space } from '~/components';
import Table from '~/components/Table';
import theme from '~/constants/theme';
import { useGetContactsApi, useMutateContactsApi } from '~/controllers/contacts';
import { useGetApplication } from '~/controllers/wizard';
import { CheckThinIcon } from '~/img';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { Draft } from '~/types/Draft';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Shareholders } from '~/types/Shareholders';
import { contactObjCreation, getUpdatedContact, getUpdatedShareholderDataSync } from '~/utils/contacts';
import { getTableRowsErros } from '~/utils/getTableRowsErros';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';

import BeneficialOwnerAdd from './BeneficialOwnerAdd';
import BeneficialOwnerEdit from './BeneficialOwnerEdit';
import BeneficialOwnersCardMobile from './BeneficialOwnersCardMobile';
import { isShareholderEditButtonDisabled } from './utils';

const BeneficialOwnerTable: React.FC<Partial<FormItemInjectedProps<Shareholders>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<Shareholders>;
  const allContacts = useGetContactsApi();
  const { refetch: refetchContacts } = allContacts;
  const shareholdersFormState = { shareholders: value };
  const globeState = useSelector((state: any) => state?.state);
  const { commonFormErrors } = globeState;
  const tableRowErrors = getTableRowsErros({
    commonFormErrors: commonFormErrors['/wizard/application/shareholders'],
    tableKey: 'beneficialOwners',
  });
  const { errorRowsKeys } = tableRowErrors;
  const { t } = useTranslation('fillApplication');

  const applicationQuery = useGetApplication();
  const hasBeneficialOwnersValidationWarnings = hasSoftDeclineFields(
    applicationQuery.data,
    'shareholders.beneficialOwners[]',
  );

  const individualColumns: ColumnsType<IndividualRowType> = useMemo(
    () => [
      {
        title: t('First name'),
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: t('Last name'),
        dataIndex: 'lastName',
        key: 'lastName',
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Emirates ID'),
        dataIndex: 'hasEmiratesId',
        key: 'hasEmiratesId',
        render: (hasEmiratesId: boolean) => {
          if (hasEmiratesId) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Shares %'),
        dataIndex: 'shares',
        key: 'shares',
      },
      {
        title: '',
        key: 'action',
        fixed: 'right',
        render: (_, { onDelete, onEdit }, index) => (
          <div style={actionsStyle}>
            <Space size={16}>
              <EditButton
                id="beneficialOwnerTable_EditButton"
                disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasBeneficialOwnersValidationWarnings)}
                hasValidationWarnings={hasBeneficialOwnersValidationWarnings}
                onClick={onEdit}
                hasError={errorRowsKeys?.includes(`${index}`)}
              />
              <DeleteButton
                id="beneficialOwnerTable_DeleteButton"
                disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasBeneficialOwnersValidationWarnings)}
                onClick={onDelete}
              />
            </Space>
          </div>
        ),
      },
    ],
    [hasBeneficialOwnersValidationWarnings, applicationQuery.data, t, errorRowsKeys],
  );

  const [editId, setId] = useState('');
  const [sourceOfEditing, setSourceOfEditing] = useState('users');
  const openEdit = (uid: string) => {
    setId(uid);
  };
  const closeEdit = () => {
    setId('');
  };

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  const contactMutation = useMutateContactsApi();

  if (!applicationQuery.data) {
    return null;
  }
  const payload = applicationQuery.data?.payload;

  const individualDataSource = getAllIndividualsData(
    shareholdersFormState,
    onChange,
    openEdit,
    refetchContacts,
    sourceOfEditing,
    contactMutation,
    allContacts,
  );

  if (
    isEmpty(shareholdersFormState.shareholders?.businesses || []) &&
    isEmpty(shareholdersFormState.shareholders?.beneficialOwners || [])
  )
    return null;

  return (
    <>
      <BeneficialOwnerEdit
        uid={editId}
        onClose={closeEdit}
        {...props}
        setSourceOfEditing={setSourceOfEditing}
        refetchContacts={refetchContacts}
      />
      <Typography.Title level={4}>{t('Beneficial Owners')}</Typography.Title>
      <Typography.Paragraph>
        {t(
          'Because shares are held by a Legal entity as disclosed in the above section, please provide the details of Beneficial Owners of the entity.',
        )}
      </Typography.Paragraph>
      <Space size={24} direction="vertical">
        {!isEmpty(individualDataSource) && (
          <>
            {!isTablet ? (
              <Table
                id="beneficialOwnersTable"
                columns={individualColumns}
                dataSource={individualDataSource}
                pagination={pagination}
                rowKey="uid"
              />
            ) : (
              <Space size={12} direction="vertical">
                {individualDataSource.map((item) => (
                  <BeneficialOwnersCardMobile
                    key={item.uid}
                    disableRowActions={isShareholderEditButtonDisabled(
                      applicationQuery.data,
                      hasBeneficialOwnersValidationWarnings,
                    )}
                    hasBeneficialOwnersValidationWarnings={hasBeneficialOwnersValidationWarnings}
                    {...item}
                  />
                ))}
              </Space>
            )}
          </>
        )}
        {!isEmpty(shareholdersFormState.shareholders?.businesses ?? []) && (
          <BeneficialOwnerAdd setSourceOfEditing={setSourceOfEditing} {...props} />
        )}
      </Space>
    </>
  );
};

const pagination = { hideOnSinglePage: true };

type IndividualRowType = {
  uid: string;
  firstName: string | undefined;
  lastName: string | undefined;
  hasPassport: boolean;
  hasEmiratesId: boolean;
  shares: number | undefined;
  onDelete: () => void;
  onEdit?: () => void;
};

const getAllIndividualsData = (
  shareholdersDraft: Draft<'shareholders'> | undefined,
  onFormChange: (value: Shareholders) => void,
  openEdit: (uid: string) => void,
  refetchContacts: any,
  sourceOfEditing: string,
  contactMutation: UseMutationResult<any, unknown, any, void>,
  allContacts?: any,
): IndividualRowType[] => {
  const dataSource = sourceOfEditing === 'shareholder' ? refetchContacts : allContacts;

  const updatedData: any = getUpdatedShareholderDataSync(
    { existedData: { beneficialOwners: shareholdersDraft?.shareholders?.beneficialOwners } },
    dataSource?.data,
  );

  return (
    updatedData?.beneficialOwners?.map((item: any) => {
      return {
        uid: item?.data?.uid as string,
        firstName: item?.data?.firstName,
        lastName: item?.data?.lastName,
        hasPassport: !isEmpty(item?.data?.passport?.files),
        hasEmiratesId: !isEmpty(item?.data?.emiratesId?.files),
        shares: item?.shares,
        onDelete: async () => {
          const updatedContact = await getUpdatedContact(item.data);
          const toDeleteIndividual = contactObjCreation(updatedContact, {
            type: 'DELETE',
            role: 'beneficialOwners',
          });
          contactMutation?.mutate(toDeleteIndividual);
          onFormChange(
            produce(shareholdersDraft, (draft) => {
              draft!.shareholders!.beneficialOwners = draft!.shareholders!.beneficialOwners!.filter(
                (a) => a?.data?.uid !== item?.data?.uid,
              );
            })?.shareholders as Shareholders,
          );
        },
        onEdit: () => item?.data?.uid && openEdit(item.data.uid),
      };
    }) ?? []
  );
};

const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };

export default BeneficialOwnerTable;
